/* eslint complexity: 0 */
import { create_handoff_drawer } from './onboarding_handoff_base_helpers';

function handoff_entity_route(entity) {
  const entity_route_map = {
    'ABC Tech Solutions': 'SupplierRegister',
    'NewGX Capital': 'ContractLifecycle',
    'Helios Mining': 'ContractLifecycle',
    'Lulama Trading': 'SupplierRegister',
    'Kabo Sithole': 'EmployeeRegister'
  };
  const matched_key = Object.keys(entity_route_map).find((match) => entity.includes(match));
  const matched_route = matched_key ? Reflect.get(entity_route_map, matched_key) : null;
  return matched_route || 'OnboardingHandoff';
}

function build_obligation_stats(row, is_attention) {
  const attention_accent = is_attention ? 'orange' : 'green';
  return [
    { label: 'Entity', value: row.entity, note: row.type, accent: attention_accent },
    { label: 'Due', value: row.due, note: row.type, accent: attention_accent },
    { label: 'Status', value: row.status, note: is_attention ? 'Action required' : 'In watchlist', accent: is_attention ? 'red' : 'green' },
    { label: 'Owner', value: row.entity, note: 'Monitoring queue', accent: 'green' },
    { label: 'Routing', value: row.type, note: 'Lifecycle task', accent: 'green' }
  ];
}

function obligation_documents(row, is_attention, status_class) {
  return [
    { title: row.obligation, meta: `${row.entity} - ${row.type}`, status: row.status, status_class, icon: is_attention ? 'bi-exclamation-circle' : 'bi-check2-circle' },
    { title: 'Due date', meta: row.due, status: row.status, status_class: 'bg-secondary-subtle text-secondary-emphasis', icon: 'bi-calendar2-event' },
    { title: 'Monitoring type', meta: row.type, status: row.status, status_class: 'bg-secondary-subtle text-secondary-emphasis', icon: 'bi-eye' }
  ];
}

function obligation_approvals(row, is_attention, route_name) {
  return [
    { title: 'Captured in post-onboarding queue', subtitle: `${row.entity} - ${row.obligation}`, icon: 'bi-check2-circle' },
    { title: is_attention ? 'Follow-up needed' : 'Monitoring active', subtitle: route_name, icon: is_attention ? 'bi-exclamation-circle' : 'bi-check2-circle' }
  ];
}

function obligation_downstream(row, is_attention, route_name) {
  return [
    { title: 'Compliance record updated', meta: `${row.entity} - ${row.type}`, status: is_attention ? 'Pending' : 'Tracked', status_class: is_attention ? 'bg-warning-subtle text-warning-emphasis' : 'bg-success-subtle text-success-emphasis' },
    { title: 'Go to owning register', meta: route_name, status: 'Open', status_class: 'bg-secondary-subtle text-secondary-emphasis' }
  ];
}

function obligation_payload(row, is_attention, route_name, status_class) {
  const payload = {
    section: `OBLIGATION - ${row.entity.toUpperCase()} - ${row.obligation.toUpperCase()}`,
    title: row.obligation,
    subtitle: `${row.entity} - ${row.type} - due ${row.due}`,
    status: row.status,
    status_class,
    intro: is_attention
      ? 'This obligation still needs action and should stay on the monitoring queue until it is satisfied.'
      : 'This obligation is active in the ongoing compliance lifecycle and should be monitored on schedule.',
    stats: build_obligation_stats(row, is_attention),
    risk_label: 'Obligation posture',
    risk_status: row.status,
    risk_status_class: status_class,
    risk_tags: [row.type, `Due ${row.due}`, is_attention ? 'Needs intervention' : 'On schedule'],
    documents_label: 'Obligation details',
    documents_status: row.status,
    documents: obligation_documents(row, is_attention, status_class),
    approvals_label: 'Lifecycle actions',
    approvals_status: is_attention ? 'Action required' : 'Scheduled',
    approvals: obligation_approvals(row, is_attention, route_name),
    obligations_label: 'Monitoring items',
    obligations_status: is_attention ? 'Open' : 'Scheduled',
    obligations: [{ title: row.obligation, subtitle: `${row.type} - due ${row.due}`, status: row.status, status_class }],
    downstream_label: 'Connected actions',
    downstream_status: is_attention ? 'Pending' : 'Tracked',
    downstream: obligation_downstream(row, is_attention, route_name),
    footer_primary_label: 'Open record',
    footer_primary_route_name: route_name,
    footer_secondary_label: 'Close'
  };
  return payload;
}

export function create_obligation_drawer(row) {
  const status = String(row.status || '').toLowerCase();
  const status_class = row.status_class || 'bg-secondary-subtle text-secondary-emphasis';
  const is_attention = ['overdue', 'blocked', 'pending'].includes(status);
  const route_name = handoff_entity_route(row.entity);
  return create_handoff_drawer(obligation_payload(row, is_attention, route_name, status_class));
}

function build_renewal_stats(row, is_due_now, route_name) {
  const due_accent = is_due_now ? 'orange' : 'green';
  return [
    { label: 'Entity', value: row.entity, note: row.item, accent: 'green' },
    { label: 'Review date', value: row.review_date, note: 'Next action', accent: is_due_now ? 'red' : 'green' },
    { label: 'Expiry', value: row.expiry_date, note: 'Calendar event', accent: due_accent },
    { label: 'Status', value: row.status, note: is_due_now ? 'Due immediately' : 'Scheduled', accent: due_accent },
    { label: 'Route', value: route_name, note: 'Owning register', accent: 'green' }
  ];
}

function renewal_documents(row, is_due_now, status_class) {
  return [
    { title: row.item, meta: `${row.entity} - renewal`, status: row.status, status_class, icon: is_due_now ? 'bi-calendar2-x' : 'bi-calendar2-event' },
    { title: 'Review date', meta: row.review_date, status: row.status, status_class: 'bg-secondary-subtle text-secondary-emphasis', icon: 'bi-bell' },
    { title: 'Expiry date', meta: row.expiry_date, status: row.status, status_class: 'bg-secondary-subtle text-secondary-emphasis', icon: 'bi-calendar2-check' }
  ];
}

function renewal_approvals(row, is_due_now, route_name) {
  return [
    { title: 'Added to compliance calendar', subtitle: `${row.entity} - ${row.item}`, icon: 'bi-check2-circle' },
    { title: is_due_now ? 'Escalate immediately' : 'Review on schedule', subtitle: route_name, icon: is_due_now ? 'bi-exclamation-circle' : 'bi-check2-circle' }
  ];
}

function renewal_downstream(row, is_due_now, route_name) {
  return [
    { title: 'Calendar entry updated', meta: `${row.entity} - ${row.expiry_date}`, status: is_due_now ? 'Pending' : 'Tracked', status_class: is_due_now ? 'bg-warning-subtle text-warning-emphasis' : 'bg-success-subtle text-success-emphasis' },
    { title: 'Owning register updated', meta: route_name, status: 'Open', status_class: 'bg-secondary-subtle text-secondary-emphasis' }
  ];
}

function renewal_payload(row, is_due_now, route_name, status_class) {
  const payload = {
    section: `RENEWAL - ${row.entity.toUpperCase()} - ${row.item.toUpperCase()}`,
    title: row.item,
    subtitle: `${row.entity} - review ${row.review_date} - expiry ${row.expiry_date}`,
    status: row.status,
    status_class,
    intro: is_due_now
      ? 'This renewal is due now and should be treated as the highest-priority item in the calendar.'
      : 'This renewal is scheduled and will drive the next compliance review cycle when it comes due.',
    stats: build_renewal_stats(row, is_due_now, route_name),
    risk_label: 'Renewal posture',
    risk_status: row.status,
    risk_status_class: status_class,
    risk_tags: [row.review_date === 'Immediate' ? 'Review now' : `Review ${row.review_date}`, `Expires ${row.expiry_date}`, route_name],
    documents_label: 'Renewal details',
    documents_status: row.status,
    documents: renewal_documents(row, is_due_now, status_class),
    approvals_label: 'Lifecycle actions',
    approvals_status: is_due_now ? 'Due now' : 'Scheduled',
    approvals: renewal_approvals(row, is_due_now, route_name),
    obligations_label: 'Renewal actions',
    obligations_status: is_due_now ? 'Open' : 'Tracked',
    obligations: [{ title: row.item, subtitle: `Review ${row.review_date}`, status: row.status, status_class }],
    downstream_label: 'Connected actions',
    downstream_status: is_due_now ? 'Pending' : 'Scheduled',
    downstream: renewal_downstream(row, is_due_now, route_name),
    footer_primary_label: 'Open record',
    footer_primary_route_name: route_name,
    footer_secondary_label: 'Close'
  };
  return payload;
}

export function create_renewal_drawer(row) {
  const status = String(row.status || '').toLowerCase();
  const status_class = row.status_class || 'bg-secondary-subtle text-secondary-emphasis';
  const is_due_now = status === 'immediate';
  const route_name = handoff_entity_route(row.entity);
  return create_handoff_drawer(renewal_payload(row, is_due_now, route_name, status_class));
}
